import {
  Box,
  ChevronDown,
  ChevronUp,
  Flex,
  Heading,
  Information,
  Pressable,
  Text,
} from "../../../../../../mads";
import { LinearGradient as ExpoLinearGradient } from "../../../../../../shared/components/LinearGradient/linear-gradient";
import { RichText } from "../../../../../../shared/components/RichText";
import { useI18n } from "../../../../../../shared/I18n";
import { logTapButtonWithExtraParams } from "../../../../../../shared/native-modules/tare/logTapButtonWithExtraParams";
import { useViewJobDataContext } from "../../../../context/ViewJobDataContext";
import { ViewJobDivider } from "../../Divider";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import { useNavigation } from "@react-navigation/native";
import React, { useCallback, useRef, useState } from "react";
import { Animated, Easing, type ViewStyle } from "react-native";

const COLLAPSED_HEIGHT = 160;
const MAX_EXPANDED_HEIGHT = 2000;
const FADE_HEIGHT = 120;

const fadeOverlayStyle: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: FADE_HEIGHT,
};

function AIDisclaimerButton({ withPremiumSponsorship }: { withPremiumSponsorship: boolean }) {
  const i18n = useI18n();
  const navigation = useNavigation();
  const iconTopMargin = withPremiumSponsorship ? 2 : 6;

  return (
    <Pressable
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      onPress={() => navigation.navigate("CompanySummaryDisclaimerModal" as never)}
      accessibilityRole="button"
      accessibilityLabel={i18n.pgettext(
        "AI company summary info button",
        "More info about AI-generated summary",
      )}
      sx={{ marginInlineStart: spacing.s1, marginBlockStart: iconTopMargin }}
    >
      <Information size="xs" variant="stroke" fill={colors.neutral1000} aria-hidden />
    </Pressable>
  );
}

interface CompanySummaryProps {
  companySummaryText: string;
  withPremiumSponsorship?: boolean;
}

export function CompanySummary({
  companySummaryText,
  withPremiumSponsorship = false,
}: CompanySummaryProps) {
  const i18n = useI18n();
  const { initialData } = useViewJobDataContext();
  const fccKey = initialData?.companyTabModel?.fccKey;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const maxHeightAnim = useRef(new Animated.Value(COLLAPSED_HEIGHT)).current;
  const fadeOpacity = useRef(new Animated.Value(1)).current;

  const match = companySummaryText.match(/^\*\*(.*?)\*\*\n\n([\s\S]*)$/);

  const handleToggle = useCallback(() => {
    const expanding = isCollapsed;
    setIsCollapsed(!expanding);

    logTapButtonWithExtraParams(
      "rn-view-job-employer-insights",
      "company-summary-toggle",
      ["viewjob"],
      { expanded: expanding },
    ).catch(console.error);

    if (expanding) {
      Animated.parallel([
        Animated.timing(maxHeightAnim, {
          toValue: MAX_EXPANDED_HEIGHT,
          duration: 300,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(fadeOpacity, {
          toValue: 0,
          duration: 200,
          delay: 0,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(maxHeightAnim, {
          toValue: COLLAPSED_HEIGHT,
          duration: 300,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(fadeOpacity, {
          toValue: 1,
          duration: 200,
          delay: 100,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [isCollapsed, maxHeightAnim, fadeOpacity]);

  if (!match) {
    console.error(
      `CompanySummary: malformed summary text could not be parsed for fccKey=${fccKey}`,
    );
    return null;
  }

  const heading = match[1];
  const body = match[2];
  const headingLevel = withPremiumSponsorship ? 2 : 4;
  const headingMargin = withPremiumSponsorship ? spacing.s3 : spacing.s4;
  const buttonTopSpacing = isCollapsed ? 0 : spacing.s2;

  return (
    <Box>
      <Flex sx={{ flexDirection: "row", alignItems: "flex-start", marginBlockEnd: headingMargin }}>
        <Heading level={headingLevel} sx={{ marginBlock: 0, flexShrink: 1 }}>
          {heading}
        </Heading>
        <AIDisclaimerButton withPremiumSponsorship={withPremiumSponsorship} />
      </Flex>
      <Animated.View style={{ maxHeight: maxHeightAnim, overflow: "hidden", position: "relative" }}>
        <RichText
          content={body}
          level={2}
          sx={{ color: colors.neutral1000, marginBlockEnd: buttonTopSpacing }}
        />
        <Animated.View style={[fadeOverlayStyle, { opacity: fadeOpacity }]} pointerEvents="none">
          <ExpoLinearGradient
            colors={["rgba(255, 255, 255, 0.0)", "rgba(255, 255, 255, 1.0)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={{ flex: 1 }}
            pointerEvents="none"
          />
        </Animated.View>
      </Animated.View>
      <Pressable
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={isCollapsed ? i18n.gettext("Show more") : i18n.gettext("Show less")}
        sx={{
          alignSelf: "flex-start",
          flexDirection: "row",
          alignItems: "center",
          marginBlockEnd: spacing.s4,
        }}
      >
        <Text
          level={3}
          sx={{
            color: colors.neutral800,
            borderWidth: 0,
            borderBottomWidth: 1,
            borderStyle: "dotted",
            marginBlockStart: 0,
          }}
        >
          {isCollapsed ? i18n.gettext("Show more") : i18n.gettext("Show less")}
        </Text>
        <Box sx={{ marginBlockStart: 8 }}>
          {isCollapsed ? (
            <ChevronDown size="sm" color={colors.neutral800} />
          ) : (
            <ChevronUp size="sm" color={colors.neutral800} />
          )}
        </Box>
      </Pressable>
      <ViewJobDivider />
    </Box>
  );
}
